import React from 'react';
import CryptoJS from 'crypto-js';
import { getDateTime } from 'utils/dateHelper';
import { getVerbiage } from 'utils/stringHelper';
import { useSelector, useDispatch} from 'react-redux';
import { RootState } from 'reducers';
import { getFiservConfig } from 'utils/applicationHelper';

import { ReservationStatusList } from 'models/backend/constant/Reservation';
import { updateFiservCheckoutPaymentResponse } from 'reducers/payment';
import { createFiservCheckoutPaymentAPI } from 'services/paymentService';
import { postErrorLog } from 'services/errorLogService';
import { ARIA_SuccessCode } from 'models';

interface IProps {
  onSubmit: () => void;
  isPreAuthCard: boolean;
  onRetrieveConfigCompleted: (isCompleted: boolean) => void;
}

export const FiservForm: React.FC<IProps> = ({onRetrieveConfigCompleted, onSubmit, isPreAuthCard}) => {
    const { verbiages } = useSelector((state: RootState) => state.verbiages);
    const formRef = React.useRef(null);
    const dispatch = useDispatch();
    const { appConfig } = useSelector((state: RootState) => state.appConfigState);
    const { selectedHotel } = useSelector((state: RootState) => state.hotelState);
    const { selectedReservation } = useSelector((state: RootState) => state.reservationState);
    const { preAuthCardInfo } = useSelector((state: RootState) => state.paymentState);
    const { fiservCheckoutPaymentResponse } = useSelector((state: RootState) => state.paymentState);

    const [authenticateTransaction, setAuthenticateTransaction] = React.useState("");
    const [chargetotal, setChargetotal] = React.useState("");
    const [checkoutoption, setCheckoutoption] = React.useState("");
    const [currency, setCurrency] = React.useState("");
    const [hashAlgorithm, setHashAlgorithm] = React.useState("");
    const [language, setLanguage] = React.useState("");
    const [responseFailURL, setResponseFailURL] = React.useState("");
    const [responseSuccessURL, setResponseSuccessURL] = React.useState("");
    const [sharedsecret, setSharedSecret] = React.useState("");
    const [storename, setStorename] = React.useState("");
    const [timezone, setTimezone] = React.useState("");
    const [transactionNotificationURL, setTransactionNotificationURL] = React.useState("");
    const [txndatetime, setTxndatetime] = React.useState("");
    const [txntype, setTxntype] = React.useState("");
    const [oid, setOid] = React.useState("");
    const [hashExtended, setHashExtended] = React.useState<string>("");
    
    React.useEffect(()=>{
        if (selectedHotel != undefined && selectedReservation != undefined && appConfig != undefined) {
            onRetrieveConfigCompleted(false);
            createFiservCheckoutPaymentAPI({
                guid: selectedReservation?.guid
                })
                .then((response) => {
                    if (response.resultCode == ARIA_SuccessCode) {
                        dispatch(updateFiservCheckoutPaymentResponse(response.result));
                        
                        setCurrency(response.result.currency.toString());
                        onRetrieveConfigCompleted(true);            
                    } 
                    else {
                        alert(getVerbiage(verbiages, 'webmsg014'));                        
                        onRetrieveConfigCompleted(false);
                    }
                })
                .catch((err) => {
                    postErrorLog({
                        confirmationNo: selectedReservation?.confirmationNo,
                        errorMessage: JSON.stringify(err)
                    })
                    alert(getVerbiage(verbiages, 'webmsg014'));
                    onRetrieveConfigCompleted(false);
                });
        }
    }, []);
    
    React.useEffect(()=>{
        if (selectedHotel != undefined && selectedReservation != undefined && appConfig != undefined) {
            var config = getFiservConfig();
            setTxndatetime(getDateTime(new Date()));  
            setAuthenticateTransaction(config.authenticateTransaction);
            setHashAlgorithm(config.hashAlgorithm);
            setLanguage(config.language);
            setResponseFailURL(appConfig?.fiservPaymentConfig.responseFailURL);
            setResponseSuccessURL(appConfig?.fiservPaymentConfig.responseSuccessURL);
            setSharedSecret(appConfig?.fiservPaymentConfig.sharedSecret);
            setStorename(appConfig?.fiservPaymentConfig.storename);
            setTimezone(appConfig?.fiservPaymentConfig.timezone);
            setTransactionNotificationURL(appConfig?.fiservPaymentConfig.transactionNotificationURL);
            if(fiservCheckoutPaymentResponse != undefined){
                setChargetotal(fiservCheckoutPaymentResponse.amount.toString());
                if(selectedReservation.reservationStatus.toUpperCase() == ReservationStatusList[2].value){
                    if(isPreAuthCard && preAuthCardInfo != undefined)
                    {
                        setOid(preAuthCardInfo.referenceNo);
                        setCheckoutoption("");
                        setTxntype(config.txntype.checkOut.postauth);
                        const stringToExtendedHash = (`${authenticateTransaction}|${chargetotal}|${currency}|${hashAlgorithm}|${language}|${oid}|${responseFailURL}|${responseSuccessURL}|${storename}|${timezone}|${transactionNotificationURL}|${txndatetime}|${txntype}`);
                        const extendedHash = CryptoJS.HmacSHA256(stringToExtendedHash, sharedsecret);
                        setHashExtended(CryptoJS.enc.Base64.stringify(extendedHash));
                    }
                    else
                    {
                        setOid(fiservCheckoutPaymentResponse.referenceNo);
                        setCheckoutoption(config.checkoutoption);
                        setTxntype(config.txntype.checkOut.sale);
                        const stringToExtendedHash = (`${authenticateTransaction}|${chargetotal}|${checkoutoption}|${currency}|${hashAlgorithm}|${language}|${oid}|${responseFailURL}|${responseSuccessURL}|${storename}|${timezone}|${transactionNotificationURL}|${txndatetime}|${txntype}`);
                        const extendedHash = CryptoJS.HmacSHA256(stringToExtendedHash, sharedsecret);
                        setHashExtended(CryptoJS.enc.Base64.stringify(extendedHash));
                    }
                }
                else
                {
                    setOid(fiservCheckoutPaymentResponse.referenceNo);
                    setCheckoutoption(config.checkoutoption);
                    setTxntype(config.txntype.checkIn);
                    const stringToExtendedHash = (`${authenticateTransaction}|${chargetotal}|${checkoutoption}|${currency}|${hashAlgorithm}|${language}|${oid}|${responseFailURL}|${responseSuccessURL}|${storename}|${timezone}|${transactionNotificationURL}|${txndatetime}|${txntype}`);
                    const extendedHash = CryptoJS.HmacSHA256(stringToExtendedHash, sharedsecret);
                    setHashExtended(CryptoJS.enc.Base64.stringify(extendedHash));
                }
            }
        }
    },[fiservCheckoutPaymentResponse, preAuthCardInfo, isPreAuthCard, txndatetime]);
    
    return (
        <form style={{display:'none'}} method="post" action={appConfig?.fiservPaymentConfig.url} id="fiservForm" ref={formRef} onSubmit={onSubmit}>
            <p>
                <label htmlFor="authenticateTransaction">Authenticate Transaction:</label> 
                <input type="text" name="authenticateTransaction" value={authenticateTransaction} id="authenticateTransaction" readOnly/>  
            </p>
            <p>
                <label htmlFor="HashExtended">Hash Extended:</label> 
                <input type="text" name="hashExtended" value={hashExtended} id="hashExtended" readOnly/>
            </p>
            <p>
                <label htmlFor="txntype">Transaction type:</label>  
                <input  type="text" name="txntype" value={txntype} id="txntype" readOnly/>
            </p>
            <p>
                <label htmlFor="timezone">Timezone:</label>  
                <input type="text" name="timezone" value={timezone} id="timezone" readOnly/>
            </p>
            {(selectedReservation?.reservationStatus.toUpperCase() == ReservationStatusList[0].value) ? (
                <p>
                    <label htmlFor="checkoutoption">Checkoutoption:</label>  
                    <input type="text" name="checkoutoption" value={checkoutoption} id = "checkoutoption" readOnly/>
                </p>
            ) : isPreAuthCard? null : (
                <p>
                    <label htmlFor="checkoutoption">Checkoutoption:</label>  
                    <input type="text" name="checkoutoption" value={checkoutoption} id = "checkoutoption" readOnly/>
                </p>
                )
            }
            <p>
                <label htmlFor="txndatetime">Txndatetime:</label>  
                <input type="text" name="txndatetime" value={txndatetime} id="txndatetime" readOnly/>
            </p>
            <p>
                <label htmlFor="hash_algorithm">Hash_algorithm:</label>  
                <input type="text" name="hash_algorithm" value={hashAlgorithm} id="hash_algorithm" readOnly/>
            </p>
            <p>
                <label htmlFor="storename">Storename:</label>  
                <input type="text" name="storename" value={storename} id="storename" readOnly/>
            </p>
            <p>
                <label htmlFor="chargetotal">Chargetotal:</label>  
                <input type="text" name="chargetotal" value={chargetotal} id="chargetotal" readOnly/>
            </p>
            <p>
                <label htmlFor="currency">Currency:</label>  
                <input type="text" name="currency" value={currency} id="currency" readOnly/>
            </p>
            <p>
                <label htmlFor="language">Language:</label>  
                <input type="text" name="language" value={language} id="language" readOnly/>
            </p>
            <p>
                <label htmlFor="oid">Order id:</label>  
                <input type="text" name="oid" value={oid} id="oid" readOnly/>
            </p>
            <p>
                <label htmlFor="responseFailURL">ResponseFailURL:</label>  
                <input type="text" name="responseFailURL" value={responseFailURL} id="responseFailURL" readOnly/>
            </p>
            <p>
                <label htmlFor="responseSuccessURL">ResponseSuccessURL:</label>  
                <input type="text" name="responseSuccessURL" value={responseSuccessURL} id="responseSuccessURL" readOnly/>
            </p>
            <p>
                <label htmlFor="transactionNotificationURL">TransactionNotificationURL:</label>  
                <input type="text" name="transactionNotificationURL" value={transactionNotificationURL} id="transactionNotificationURL" readOnly/>
            </p>
        </form>
    )
};
